import { useState } from "react"

import { useTranslation } from "react-i18next"

import type { MessageVisibilityMode } from "@/api/models"
import type { Participant, ParticipantRole } from "@/services/project-service"

import { Crown, LogOut, ShieldCheck, Trash2, User, Users } from "@/components/icons"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { CATEGORY_TONES } from "@/components/ui/tone-classes"
import { cn } from "@/lib/utils"

type ParticipantListItemProps = {
  participant: Participant
  canManage: boolean
  isCurrentUser?: boolean
  isCurrentUserOwner?: boolean
  isOwner?: boolean
  entityType?: "project" | "document" | "chat"
  allowedRoles?: ParticipantRole[] // Optional: limit which roles can be selected
  showAccessMode?: boolean // Show message access mode (for chat participants)
  latestMessageId?: string // Latest message ID for chat sessions (used when changing access mode)
  selectedMessageId?: string // Selected message ID (used when switching to specific message)
  onRoleChange?: (role: ParticipantRole) => void
  onAccessModeChange?: (mode: MessageVisibilityMode, messageId?: string) => void
  onRemove?: () => void
  onLeave?: () => void
  onTransferOwnership?: () => void
}

export function ParticipantListItem({
  participant,
  canManage,
  isCurrentUser = false,
  isCurrentUserOwner = false,
  isOwner = false,
  entityType = "project",
  allowedRoles,
  showAccessMode = false,
  latestMessageId,
  selectedMessageId,
  onRoleChange,
  onAccessModeChange,
  onRemove,
  onLeave,
  onTransferOwnership,
}: ParticipantListItemProps) {
  const { t } = useTranslation()
  const [transferDialogOpen, setTransferDialogOpen] = useState(false)
  const isUser = participant.type === "user"
  const Icon = isUser ? User : Users

  // Transfer allowed for owner to non-group, non-owner participants
  const canTransferTo = isCurrentUserOwner && isUser && !isOwner && participant.role !== "owner"

  // Any non-owner direct user participant can leave (aligns with backend self-removal rule)
  const canLeave =
    entityType === "project" &&
    isCurrentUser &&
    !isOwner &&
    participant.type === "user" &&
    !!onLeave

  const roleLabels: Record<ParticipantRole, string> = {
    reader: t("sharing.role_reader", { defaultValue: "Reader" }),
    editor: t("sharing.role_editor", { defaultValue: "Editor" }),
    admin: t("sharing.role_admin", { defaultValue: "Admin" }),
    owner: t("sharing.role_owner", { defaultValue: "Owner" }),
  }

  // Access mode labels for chat participants
  const accessModeLabels: Record<MessageVisibilityMode, string> = {
    full_history: t("sharing.access_full_history_badge"),
    partial_history: t("sharing.access_partial_history_badge"),
    specific_message: t("sharing.access_specific_message_badge"),
  }

  // Get current access mode from participant (for chat participants)
  const currentAccessMode =
    showAccessMode && "messageAccessMode" in participant
      ? (participant.messageAccessMode as MessageVisibilityMode)
      : undefined

  const handleTransferConfirm = () => {
    setTransferDialogOpen(false)
    onTransferOwnership?.()
  }

  let transferOwnershipDescription: string
  if (showAccessMode) {
    transferOwnershipDescription = t("sharing.transfer_ownership_description_chat", {
      defaultValue:
        "Are you sure you want to transfer ownership of this chat session to {{name}}? You will become a reader with full chat history access.",
      name: participant.displayName,
    })
  } else if (entityType === "document") {
    transferOwnershipDescription = t("sharing.transfer_ownership_description_document", {
      defaultValue:
        "Are you sure you want to transfer ownership of this document to {{name}}? You will become a reader and will no longer be able to edit or delete this document.",
      name: participant.displayName,
    })
  } else {
    transferOwnershipDescription = t("sharing.transfer_ownership_description", {
      defaultValue:
        "Are you sure you want to transfer ownership of this project to {{name}}? You will become an admin and will no longer be able to delete the project.",
      name: participant.displayName,
    })
  }

  return (
    <>
      <div
        className={cn(
          "flex items-center gap-3 rounded-md px-3 py-2",
          isCurrentUser && "bg-accent/50"
        )}
      >
        {/* Icon */}
        <div
          className={cn(
            "flex size-8 shrink-0 items-center justify-center rounded-full",
            isUser ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
          )}
        >
          <Icon className="size-4" />
        </div>

        {/* Info */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate font-medium">
              {participant.displayName}
              {isCurrentUser && (
                <span className="text-muted-foreground ml-1 text-xs">
                  ({t("sharing.you", { defaultValue: "you" })})
                </span>
              )}
            </span>
            {isOwner && (
              <span title={t("sharing.owner", { defaultValue: "Owner" })}>
                <Crown className="text-warning-ink size-4 shrink-0" />
              </span>
            )}
          </div>
          <span className="text-muted-foreground block text-xs">
            {participant.type === "group"
              ? t("sharing.group", { defaultValue: "Group" })
              : t("sharing.user", { defaultValue: "User" })}
            {showAccessMode &&
            "messageAccessMode" in participant &&
            participant.messageAccessMode ? (
              <span className="ml-2">
                •{" "}
                {(() => {
                  const mode = participant.messageAccessMode
                  if (mode === "full_history") {
                    return t("sharing.access_full_history_badge")
                  }
                  if (mode === "partial_history") {
                    return t("sharing.access_partial_history_badge")
                  }
                  if (mode === "specific_message") {
                    return t("sharing.access_specific_message_badge")
                  }
                  return ""
                })()}
              </span>
            ) : null}
          </span>
        </div>

        {/* Role selector or access mode selector or badge */}
        {canManage && !isOwner && (showAccessMode || onRoleChange) ? (
          (() => {
            if (showAccessMode && currentAccessMode) {
              // Show access mode dropdown for chat participants
              return (
                <Select
                  value={currentAccessMode}
                  onValueChange={(value) => {
                    const mode = value as MessageVisibilityMode
                    let messageId: string | undefined
                    if (mode !== "full_history") {
                      // Use selected message only when explicitly switching to specific message.
                      const existingSharedId =
                        "sharedFromMessageId" in participant && participant.sharedFromMessageId
                          ? (participant.sharedFromMessageId as string)
                          : undefined
                      messageId =
                        mode === "specific_message" && selectedMessageId
                          ? selectedMessageId
                          : existingSharedId || latestMessageId
                    }
                    onAccessModeChange?.(mode, messageId)
                  }}
                >
                  <SelectTrigger className="w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full_history">{accessModeLabels.full_history}</SelectItem>
                    <SelectItem value="partial_history">
                      {accessModeLabels.partial_history}
                    </SelectItem>
                    <SelectItem value="specific_message">
                      {accessModeLabels.specific_message}
                    </SelectItem>
                  </SelectContent>
                </Select>
              )
            }
            // Show role selector for project participants
            return (
              <Select
                value={participant.role}
                onValueChange={(value) => onRoleChange?.(value as ParticipantRole)}
              >
                <SelectTrigger className="w-[110px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(!allowedRoles || allowedRoles.includes("reader")) && (
                    <SelectItem value="reader">{roleLabels.reader}</SelectItem>
                  )}
                  {(!allowedRoles || allowedRoles.includes("editor")) && (
                    <SelectItem value="editor">{roleLabels.editor}</SelectItem>
                  )}
                  {(!allowedRoles || allowedRoles.includes("admin")) && (
                    <SelectItem value="admin">{roleLabels.admin}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            )
          })()
        ) : (
          <span
            className={cn(
              "shrink-0 rounded-md px-2 py-1 text-xs font-medium",
              (() => {
                if (showAccessMode && currentAccessMode) {
                  // For chat participants, show access mode badge
                  return "bg-muted text-muted-foreground border-border border"
                }
                // For project participants, show role badge
                if (participant.role === "owner") {
                  return "bg-warning/30 text-warning-ink"
                }
                if (participant.role === "admin") {
                  return CATEGORY_TONES[1]
                }
                if (participant.role === "editor") {
                  return CATEGORY_TONES[0]
                }
                return "bg-muted text-muted-foreground border-border border"
              })()
            )}
          >
            {(() => {
              if (showAccessMode && currentAccessMode) {
                return accessModeLabels[currentAccessMode]
              }
              return roleLabels[participant.role]
            })()}
          </span>
        )}

        {/* Leave button for non-owner direct participants (project only) */}
        {canLeave && (
          <Button
            aria-label={t("sharing.leave_project", { defaultValue: "Leave project" })}
            className="text-muted-foreground hover:text-destructive size-7 shrink-0 p-0"
            size="icon"
            title={t("sharing.leave_project", { defaultValue: "Leave project" })}
            variant="ghost"
            onClick={onLeave}
          >
            <LogOut className="size-3.5" />
          </Button>
        )}

        {/* Actions dropdown for non-owner participants (managers only) */}
        {canManage && !isOwner && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="ghost">
                <span className="sr-only">{t("common.actions", { defaultValue: "Actions" })}</span>
                <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                  />
                </svg>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canTransferTo && (
                <>
                  <DropdownMenuItem onClick={() => setTransferDialogOpen(true)}>
                    <ShieldCheck className="mr-2 size-4" />
                    {t("sharing.make_owner", { defaultValue: "Make Owner" })}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem className="text-destructive" onClick={onRemove}>
                <Trash2 className="mr-2 size-4" />
                {t("sharing.remove", { defaultValue: "Remove" })}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Transfer ownership confirmation dialog */}
      <AlertDialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("sharing.transfer_ownership_title", { defaultValue: "Transfer Ownership" })}
            </AlertDialogTitle>
            <AlertDialogDescription>{transferOwnershipDescription}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel", { defaultValue: "Cancel" })}</AlertDialogCancel>
            <AlertDialogAction onClick={handleTransferConfirm}>
              {t("sharing.transfer", { defaultValue: "Transfer" })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
